import { Checkbox, Radio, styled, Switch, SwitchProps, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { StateNames, States } from 'src/Screens/Configurator';
import { checkMinMax } from 'src/utils/3d.utils';
import { changeSoleStatus, isSoleUse, createSoles } from 'src/utils/sole.utils';

type CustomSolesProps = {
  pieces: any[];
  setCustomSoles: (customSoles: any[]) => void;
  customPlatings: any[];
  customSoles: any[];
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  getValue: <T extends StateNames>(stateName: T) => States[T];
};

export const CustomSoles = ({
  pieces,
  setCustomSoles,
  customSoles,
  setValue,
  getValue,
  customPlatings,
}: CustomSolesProps) => {
  const [width, setWidth] = useState<string>();
  const [length, setLength] = useState<string>();
  const [error, setError] = useState<string>();
  const [height, setHeight] = useState<string>();

  const [holeWidth, setHoleWidth] = useState<string>();
  const [holeLength, setHoleLength] = useState<string>();
  const [poseX, setPoseX] = useState<string>();
  const [poseZ, setPoseZ] = useState<string>();
  const [frontDepth, setFrontDepth] = useState<string>();

  const [hasCustomFrontDepth, setHasCustomFrontDepth] = useState<boolean>();
  const [holeMode, setHoleMode] = useState<'dimension' | 'pose'>();
  const [mode, setMode] = useState<'closoir' | 'double closoir' | 'tete'>();

  useEffect(() => {
    if (customSoles.length > 0) {
      setWidth(
        String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.monumentWidth) * 100)),
      );
      setLength(
        String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.monumentLength) * 100)),
      );
      setHeight(String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.height) * 100)));
      setHoleWidth(
        String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.holeWidth) * 100)),
      );
      setHoleLength(
        String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.holeLength) * 100)),
      );
      setPoseX(String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.poseX) * 100)));
      setPoseZ(String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.poseZ) * 100)));
      setFrontDepth(
        String(Math.round(Number.parseFloat(customSoles[0]?.shapeDetails.frontDepth) * 100)),
      );
      if (holeMode === undefined) setHoleMode(customSoles[0]?.shapeDetails.holeMode ?? 'pose');
      if (mode === undefined) setMode(customSoles[0]?.shapeDetails.mode ?? 'closoir');
      if (hasCustomFrontDepth === undefined)
        setHasCustomFrontDepth(customSoles[0]?.shapeDetails.hasCustomFrontDepth ?? false);
    }
  }, [customSoles]);

  const updatePiece = (forceInformation?: {
    orientation?: 'x' | 'z';
    mode?: 'closoir' | 'double closoir' | 'tete';
    customSoles?: any[];
    height?: string;
    frontDepth?: string;
    holeMode?: 'dimension' | 'pose';
    hasCustomFrontDepth?: boolean;
  }) => {
    const heightToTake = forceInformation?.height ?? height;
    setError(undefined);
    createSoles(
      pieces,
      customPlatings,
      forceInformation?.customSoles ?? customSoles,
      setCustomSoles,
      {
        length: Number.parseInt(length ?? '0', 10) / 100,
        width: Number.parseInt(width ?? '0', 10) / 100,
        height: Number.parseInt(heightToTake ?? '0', 10) / 100,
        holeWidth: Number.parseInt(holeWidth ?? '0', 10) / 100,
        holeLength: Number.parseInt(holeLength ?? '0', 10) / 100,
        poseX: Number.parseInt(poseX ?? '0', 10) / 100,
        poseZ: Number.parseInt(poseZ ?? '0', 10) / 100,
        orientation: forceInformation?.orientation ?? 'z',
        holeMode: forceInformation?.holeMode ?? holeMode,
        mode: forceInformation?.mode ?? mode,
        hasCustomFrontDepth: forceInformation?.hasCustomFrontDepth ?? hasCustomFrontDepth,
        frontDepth: Number.parseInt(frontDepth ?? '0', 10) / 100,
      },
      (lengthToCheck: number) => {
        checkMinMax(lengthToCheck, customSoles[0]?.granit, setError);
      },
    );
  };

  return (
    <Container>
      {getValue('hasAdvancedMode') ? (
        <div
          style={{
            backgroundColor: '#D32F2F70',
            padding: 12,
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 12,
            marginBottom: 12,
          }}
        >
          <p style={{ fontSize: 12 }}>Attention, le mode avancé est activé.</p>
          <p style={{ fontSize: 12 }}>
            Les assemblages de pièces ne sont plus pris en compte. Vous modifierez et déplacerez
            chaque pièce indépendamment des autres.
          </p>
        </div>
      ) : null}

      <Row style={{ justifyContent: 'space-between' }}>
        <Row>
          <Typography
            variant="body2"
            color="#344054"
            style={{
              cursor: 'pointer',
              zIndex: 2,
            }}
          >
            Dimension
          </Typography>
          <Typography
            variant="body2"
            color="#344054"
            style={{ marginLeft: '4px', marginBottom: '10px', fontWeight: 700 }}
          >
            Semelle
          </Typography>
        </Row>
        <Row style={{ alignItems: 'center', gap: '6px', marginLeft: '0px' }}>
          <Typography variant="body2" color="#344054" style={{ fontWeight: 400, fontSize: '12px' }}>
            Mode avancé
          </Typography>
          <IOSSwitch
            checked={getValue('hasAdvancedMode')}
            onChange={() => setValue('hasAdvancedMode', !getValue('hasAdvancedMode'))}
          />
        </Row>
      </Row>

      {error ? (
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            paddingTop: '16px',
          }}
          color="error"
        >
          {error}
        </Typography>
      ) : null}

      <Row>
        <Row style={{ width: '60%' }}>
          <Column>
            <Label>Longueur totale</Label>
            <Row>
              <Input
                value={length}
                onChange={(e) => setLength(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              <MathSign>x</MathSign>
            </Row>
          </Column>
          <Column>
            <Label>Largeur totale</Label>
            <Row>
              <Input
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              <MathSign />
            </Row>
          </Column>
        </Row>
        <Column style={{ width: '40%' }}>
          <Label>Epaisseur</Label>
          <Input
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') updatePiece();
            }}
            onBlur={() => {
              updatePiece();
            }}
          />
        </Column>
      </Row>

      <Row style={{ width: '100%' }}>
        <Column style={{ marginRight: '10px', width: '100px', alignItems: 'flex-start' }}>
          <Label>Dimension</Label>
          <Radio
            checked={holeMode === 'dimension'}
            onClick={() => {
              setHoleMode('dimension');
              updatePiece({ holeMode: 'dimension' });
            }}
          />
        </Column>
        <Column>
          <Label>Profondeur totale trou</Label>
          <Row>
            <Input
              disabled={holeMode !== 'dimension'}
              value={holeLength}
              onChange={(e) => setHoleLength(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <MathSign>x</MathSign>
          </Row>
        </Column>
        <Column>
          <Label>Largeur totale trou</Label>
          <Row>
            <Input
              value={holeWidth}
              disabled={holeMode !== 'dimension'}
              onChange={(e) => setHoleWidth(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <MathSign />
          </Row>
        </Column>
      </Row>

      <Row style={{ width: '100%' }}>
        <Column style={{ marginRight: '10px', width: '100px', alignItems: 'flex-start' }}>
          <Label>Pose</Label>
          <Radio
            checked={holeMode === 'pose'}
            onClick={() => {
              setHoleMode('pose');
              updatePiece({ holeMode: 'pose' });
            }}
          />
        </Column>
        <Column>
          <Label>Pose Avant / Arrière</Label>
          <Row>
            <Input
              disabled={holeMode !== 'pose'}
              value={poseZ}
              onChange={(e) => setPoseZ(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <MathSign>x</MathSign>
          </Row>
        </Column>
        <Column>
          <Label>Pose Gauche / Droite</Label>
          <Row>
            <Input
              disabled={holeMode !== 'pose'}
              value={poseX}
              onChange={(e) => setPoseX(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <MathSign />
          </Row>
        </Column>
      </Row>

      <Row>
        <Column style={{ marginRight: '10px', width: '100px', alignItems: 'flex-start' }}>
          <Label>Tête avant personalisé</Label>
          <Checkbox
            value={hasCustomFrontDepth}
            checked={hasCustomFrontDepth}
            onClick={() => {
              setHasCustomFrontDepth(!hasCustomFrontDepth);
              updatePiece({ hasCustomFrontDepth: !hasCustomFrontDepth });
            }}
          />
        </Column>
        <Column>
          <Label>Dimension tête avant</Label>
          <Row>
            <Input
              disabled={!hasCustomFrontDepth}
              value={frontDepth}
              onChange={(e) => setFrontDepth(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece({ frontDepth });
              }}
              onBlur={() => {
                updatePiece({ frontDepth });
              }}
            />
          </Row>
        </Column>
      </Row>
      <Row>
        <PlatingContainer>
          <Row style={{ height: '100%' }}>
            <PlatingSvg
              src="/icons/sole-left.png"
              width={82}
              height={105}
              isActive={isSoleUse(customSoles, 'left')}
              onClick={() => {
                changeSoleStatus('left', customSoles, setCustomSoles);
                updatePiece({
                  customSoles: customSoles.map((el) => {
                    if (el.key === 'left')
                      return {
                        ...el,
                        isActive: !el.isActive,
                      };
                    return el;
                  }),
                });
              }}
            />
            <Column style={{ justifyContent: 'space-between' }}>
              <PlatingSvg
                src="/icons/sole-back.png"
                width={80}
                height={29}
                style={{ marginLeft: '5px', marginTop: '-10px', zIndex: 2 }}
                isActive={isSoleUse(customSoles, 'back')}
                onClick={() => {
                  changeSoleStatus('back', customSoles, setCustomSoles);
                  updatePiece({
                    customSoles: customSoles.map((el) => {
                      if (el.key === 'back')
                        return {
                          ...el,
                          isActive: !el.isActive,
                        };
                      return el;
                    }),
                  });
                }}
              />
              <PlatingSvg
                src="/icons/sole-front.png"
                width={117}
                height={53}
                style={{ marginLeft: '-70px', marginBottom: '35px' }}
                isActive={isSoleUse(customSoles, 'front')}
                onClick={() => {
                  changeSoleStatus('front', customSoles, setCustomSoles);
                  updatePiece({
                    customSoles: customSoles.map((el) => {
                      if (el.key === 'front')
                        return {
                          ...el,
                          isActive: !el.isActive,
                        };
                      return el;
                    }),
                  });
                }}
              />
            </Column>
            <PlatingSvg
              src="/icons/sole-right.png"
              width={50}
              height={130}
              style={{ marginLeft: '-30px', marginTop: '15px' }}
              isActive={isSoleUse(customSoles, 'right')}
              onClick={() => {
                changeSoleStatus('right', customSoles, setCustomSoles);
                updatePiece({
                  customSoles: customSoles.map((el) => {
                    if (el.key === 'right')
                      return {
                        ...el,
                        isActive: !el.isActive,
                      };
                    return el;
                  }),
                });
              }}
            />
          </Row>
          <Typography
            variant="body2"
            color="#273135"
            style={{ textAlign: 'center', fontStyle: 'italic', color: '#667085' }}
          >
            Cliquez sur une semelle pour la retirer ou l&apos;ajouter.
          </Typography>
        </PlatingContainer>
        <ModeContainer>
          <Row style={{ alignItems: 'center' }}>
            <CheckBox
              type="checkbox"
              checked={mode === 'tete'}
              onChange={() => {
                setMode('tete');
                updatePiece({ mode: 'tete' });
              }}
            />
            <Typography variant="body2" color="#273135">
              Tête avant / tête arrière
            </Typography>
          </Row>
          <Row style={{ alignItems: 'center' }}>
            <CheckBox
              type="checkbox"
              checked={mode === 'closoir'}
              onChange={() => {
                setMode('closoir');
                updatePiece({ mode: 'closoir' });
              }}
            />
            <Typography variant="body2" color="#273135">
              Closoir arrière
            </Typography>
          </Row>
          <Row style={{ alignItems: 'center' }}>
            <CheckBox
              type="checkbox"
              checked={mode === 'double closoir'}
              onChange={() => {
                setMode('double closoir');
                updatePiece({ mode: 'double closoir' });
              }}
            />
            <Typography variant="body2" color="#273135">
              Double closoir
            </Typography>
          </Row>
        </ModeContainer>
      </Row>
    </Container>
  );
};

const Container = styled('div')({
  height: '100%',
  width: '100%',
});

const Input = styled('input')({
  width: '100%',
  padding: '10px 6px',
  borderRadius: '8px',
  border: '1px solid #C2C9D1',
  '-webkit-appearance': 'none',

  color: '#273135',

  '&:focus-visible': {
    outline: '1px solid #BC9A67',
  },
});

const Column = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
});

const Label = styled('p')({
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '16px',

  marginTop: '18px',
  marginBottom: '8px',

  color: 'rgba(0, 0, 0, 0.5)',
});

const MathSign = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '10px',
  color: '#667085',
});

const Select = styled('select')({
  width: '100%',
  padding: '10px 6px',
  borderRadius: '8px',
  border: '1px solid #C2C9D1',

  color: '#273135',
});

const PlatingContainer = styled('div')({
  position: 'relative',
  marginTop: '42px',
  width: '188px',
  height: '193px',
});

const PlatingSvg = styled('img')(
  ({ width, height, isActive }: { width: number; height: number; isActive: boolean }) => ({
    width,
    height,

    cursor: 'pointer',
    filter: isActive
      ? ''
      : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%);',
  }),
);

const ModeContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  justifyContent: 'center',

  padding: '20px',
});

const CheckBox = styled('input')({
  width: '1.3em',
  height: '1.3em',
  backgroundColor: 'white',
  borderRadius: ' 50%',
  verticalAlign: 'middle',
  border: '1px solid #ddd',
  appearance: 'none',
  '-webkit-appearance': 'none',
  outline: 'none',
  cursor: 'pointer',

  padding: '8px',
  marginRight: '8px',
  '&:checked': {
    backgroundColor: '#BC9A67',
    borderColor: '#BC9A67',
  },
});

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#BC9A67' : '#BC9A67',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#BC9A67',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));
